import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'Offen', className: 'bg-yellow-100 text-yellow-800' },
  completed: { label: 'Bezahlt', className: 'bg-green-100 text-green-800' },
  failed: { label: 'Fehlgeschlagen', className: 'bg-red-100 text-red-800' },
  refunded: { label: 'Rückerstattet', className: 'bg-blue-100 text-blue-800' }
};

const paymentMethodLabels = {
  bar: 'Bar',
  rechnung: 'Rechnung',
  kreditkarte: 'Kreditkarte',
  twint: 'TWINT'
};

function CustomerTransactions({ customerId }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const formattedPayments = useMemo(() => {
    return (payments || []).map((payment) => ({
      ...payment,
      formattedDate: payment.created_at
        ? new Date(payment.created_at).toLocaleString('de-CH')
        : '-'
    }));
  }, [payments]);

  useEffect(() => {
    if (!customerId) return;

    const fetchPayments = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/payments/customer/${customerId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPayments(res.data || []);
      } catch (error) {
        console.error('Error fetching customer payments', error);
        toast.error('Fehler beim Laden der Transaktionen');
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [customerId]);

  const renderStatusBadge = (status) => {
    const cfg = statusConfig[status] || statusConfig.pending;
    return <Badge className={cfg.className}>{cfg.label}</Badge>;
  };

  const renderPaymentMethod = (method) => paymentMethodLabels[method] || method;

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-gray-500" data-testid="customer-transactions-loading">
          Lädt Transaktionen...
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle data-testid="customer-transactions-title">Transaktionen ({formattedPayments.length})</CardTitle>
      </CardHeader>
      <CardContent>
        {formattedPayments.length === 0 ? (
          <div className="text-center text-gray-500 py-6" data-testid="customer-transactions-empty">
            Noch keine Transaktionen für diesen Kunden
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="customer-transactions-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead data-testid="customer-transactions-header-date">Datum</TableHead>
                  <TableHead data-testid="customer-transactions-header-type">Typ</TableHead>
                  <TableHead data-testid="customer-transactions-header-invoice">Rechnung</TableHead>
                  <TableHead data-testid="customer-transactions-header-method">Zahlungsart</TableHead>
                  <TableHead className="text-right" data-testid="customer-transactions-header-amount">Betrag</TableHead>
                  <TableHead data-testid="customer-transactions-header-status">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {formattedPayments.map((p) => (
                  <TableRow key={p.id} data-testid={`customer-transaction-row-${p.id}`}>
                    <TableCell data-testid={`customer-transaction-date-${p.id}`}>
                      {p.formattedDate}
                    </TableCell>
                    <TableCell data-testid={`customer-transaction-type-${p.id}`}>{p.description || p.product_type || '-'}</TableCell>
                    <TableCell>
                      {p.invoice_id ? (
                        <button
                          type="button"
                          className="text-xs text-blue-600 hover:underline"
                          data-testid={`customer-transaction-invoice-${p.id}`}
                          onClick={() => {
                            const token = localStorage.getItem('token');
                            const url = `${API}/invoices/${p.invoice_id}/pdf`;
                            const win = window.open(url, '_blank');
                            if (!win) {
                              toast.error('Popup wurde blockiert. Bitte erlauben Sie Popups für diese Seite.');
                            }
                          }}
                        >
                          PDF öffnen
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400 italic" data-testid={`customer-transaction-invoice-none-${p.id}`}>Keine</span>
                      )}
                    </TableCell>
                    <TableCell data-testid={`customer-transaction-method-${p.id}`}>{renderPaymentMethod(p.payment_method)}</TableCell>
                    <TableCell className="text-right font-semibold" data-testid={`customer-transaction-amount-${p.id}`}>
                      CHF {p.amount?.toFixed(2) ?? '0.00'}
                    </TableCell>
                    <TableCell data-testid={`customer-transaction-status-${p.id}`}>{renderStatusBadge(p.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default React.memo(CustomerTransactions);
